import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import { ConfirmDialogHost } from '@/shared/components/confirm-dialog';
import { i18n } from '@/shared/i18n/i18n';
import { PwaUpdatePrompt } from '@/shared/pwa/PwaUpdatePrompt';
import { RealtimeProvider } from '@/shared/realtime';
import { ModuleRegistryContext } from '@/shared/registry/context';
import { Toaster } from '@/shared/ui/sonner';
import { TooltipProvider } from '@/shared/ui/tooltip';

import type { AppRuntime } from './runtime';

const QueryDevtools =
  import.meta.env.DEV && import.meta.env.VITE_QUERY_DEVTOOLS === '1'
    ? lazy(async () => ({ default: (await import('@tanstack/react-query-devtools')).ReactQueryDevtools }))
    : null;

/** Application-wide providers (also used by tests with a test runtime). */
export function AppProviders({ runtime, children }: { runtime: AppRuntime; children: ReactNode }) {
  return (
    <QueryClientProvider client={runtime.queryClient}>
      <I18nextProvider i18n={i18n}>
        <ModuleRegistryContext value={runtime.registry}>
          <RealtimeProvider manager={runtime.realtime}>
            <TooltipProvider>
              {children}
              <Toaster />
              <PwaUpdatePrompt />
              <ConfirmDialogHost />
            </TooltipProvider>
          </RealtimeProvider>
        </ModuleRegistryContext>
      </I18nextProvider>
      {QueryDevtools && (
        <Suspense>
          <QueryDevtools buttonPosition="bottom-left" />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
